import React, { useEffect, useState, useCallback } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { usePASAdminAuth } from '../../context/PASAdminAuthContext';
import { AgreementDocument } from '../../components/pas/AgreementDocument';
import { AgreementSignForm } from '../../components/pas/AgreementSignForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreditAgreement() {
  const navigate = useNavigate();
  const { creditLineId } = useParams();
  const clientAuth = usePASClientAuth();
  const adminAuth = usePASAdminAuth();
  const isAdmin = !!creditLineId;
  const { token, loading } = isAdmin ? adminAuth : clientAuth;
  const user = isAdmin ? adminAuth.pasAdmin : clientAuth.pasClient;
  const base = isAdmin ? `${API}/pas-banking/admin/credit-lines/${creditLineId}/agreement` : `${API}/pas-banking/credit-line/agreement`;
  const [data, setData] = useState(null);
  const [sigUrl, setSigUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);

  const load = useCallback(async () => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const r = await axios.get(base, auth);
      if (r.data.signed) {
        const img = await axios.get(`${base}/signature`, { ...auth, responseType: 'blob' });
        setSigUrl(URL.createObjectURL(img.data));
      }
      setData(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Agreement not available';
      if (isAdmin) toast.error(detail); else setMissing(detail);
    }
  }, [token, base, isAdmin]);

  useEffect(() => { if (!loading && !user) navigate(isAdmin ? '/pas/admin/login' : '/pas/login'); }, [loading, user, isAdmin, navigate]);
  useEffect(() => { if (token) load(); }, [token, load]);

  const sign = async (payload) => {
    setBusy(true);
    try {
      const r = await axios.post(`${base}/sign`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not sign'); }
    finally { setBusy(false); }
  };

  if (missing) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="agreement-no-line">
      <div className="max-w-md bg-slate-800 border border-slate-700 rounded-xl p-6 text-center space-y-4">
        <h1 className="text-lg font-bold text-white">No credit agreement yet</h1>
        <p className="text-sm text-slate-300">Your Credit Agreement and Promissory Note appear here once your credit line is approved. Subscribe as a Treasury Officer and request a credit line under Banking &amp; Credit; you can e-sign the agreement on this page as soon as it's approved.</p>
        <p className="text-xs text-slate-500">({missing})</p>
        <div className="flex gap-2 justify-center">
          <button onClick={() => navigate('/pas/banking')} data-testid="agreement-go-banking" className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg text-sm font-semibold">Go to Banking &amp; Credit</button>
          <button onClick={() => navigate('/pas/dashboard')} data-testid="agreement-go-dashboard" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Dashboard</button>
        </div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="bg-slate-800 border-b border-slate-700 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(isAdmin ? '/pas/admin/dashboard?tab=credit-lines' : '/pas/banking')} className="p-2 text-slate-400 hover:text-white" data-testid="agreement-back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-lg font-bold text-white">Subscriber Credit Agreement &amp; Promissory Note</h1>
              <p className="text-xs text-slate-400" data-testid="agreement-status">{data.signed ? 'Signed' : data.needs_resign ? `Updated agreement - re-signature required (previous ${data.prior_version})` : 'Awaiting borrower signature'}{isAdmin ? ` · ${data.credit_line.client_email}` : ''}</p>
            </div>
          </div>
          <button onClick={() => window.print()} data-testid="agreement-print-btn" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center gap-1"><Printer className="w-4 h-4" /> Print</button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8 print:p-0">
        <SectionGuide id="credit-agreement" className="mb-6" />
        {data.needs_resign && <div className="mb-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm print:hidden" data-testid="agreement-resign-banner">The credit agreement has been updated. {isAdmin ? 'The borrower must sign the new version before further draws.' : 'Sign the new version below to resume draws on your credit line. Your payment schedule is unchanged.'}</div>}
        <AgreementDocument data={data} signatureUrl={sigUrl}>
          {isAdmin ? <p className="text-sm text-gray-500">Not yet signed by the borrower.</p>
            : <AgreementSignForm defaults={data.terms.subscriber_defaults} busy={busy} onSign={sign} collateralExempt={data.collateral_exempt} />}
        </AgreementDocument>
      </main>
    </div>
  );
}
